import React, { useEffect, FC, useState, useContext } from 'react';
import { Route, Switch, withRouter, Redirect, RouteComponentProps } from 'react-router-dom';
import { Dimmer, Grid, Loader, Message, Segment, Sidebar } from 'semantic-ui-react';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';

import UserContext from './contexts/UserContext';
import RedirectContext from './contexts/RedirectContext';
import PrivateRoute from './components/components/privateRouteComponent';
import LogIn from './components/pages/login';
import ForgotPasswordPage from './components/pages/forgot-password';
import ChangeForgotPasswordPage from './components/pages/change-forgotten-password';
import ProfileSettings from './components/pages/profile-settings';
import News from './components/pages/news';
import FamilyAndFriends from './components/pages/family_friends';
import RelativeDetail from './components/components/relativeDetail';
import Settings from './components/pages/settings';
import Invites from './components/pages/invites';
import MessageRecipients from './components/pages/messageRecipients';
import MessageRecipientDetails from './components/pages/messageRecipientDetails';
import CreateMessageRecipient from './components/pages/createMessageRecipient/sso';
import Messages from './components/pages/messages';
import MessageReplyDetails from './components/pages/messages/messageDetails';
import Downloads from './components/pages/downloads';
import CommunityDownloads from './components/pages/communityDownloads';
import Activities from './components/pages/activities';
import ActivityDetail from './components/components/ActivityDetail';
import ArchivedActivities from './components/pages/archivedActivities';
import RequestDetail from './components/components/RequestDetail';
import Header from './components/components/header';
import { clearAllGreetingsSummaryCache } from './neighbors/utils/greetingsSummaryCache';
import Signup from './components/pages/signup';
import SignUpComponent from './components/components/signUpComponent'
import CommunityActivities from './components/pages/communityActivities';
import CommunityMenus from './components/pages/communityMenus';
import CommunityNotifications from './components/pages/communityNotifications';
import AutoLogin from './components/pages/auto-login';
import SSOAlexaUser from './components/pages/sso';
import SSORedirect from './components/pages/sso/redirect';
import Checkout from './components/pages/checkout';
import PublicCheckout from './components/pages/checkout/public';
import CheckoutSuccess from './components/pages/checkout/public/success';
import AddressBookDetails from './components/pages/address-book-details'
import AddressBookContactDetails from './components/pages/address-book-contacts-details'
import Support from './components/pages/support'

import { Auth } from 'aws-amplify';
import * as Authentication from './services/Authentication';
import { ToastContainer } from 'react-toastify';
import ToastCloseButton from './utils/Toast/ToastCloseButton';
import ToastBehavior from './utils/Toast/ToastBehavior';
import Reminders from './components/pages/reminders';
import * as Database from './services/db';
import AlexaLinkingLogin from './components/pages/alexa-linking';

import LegacyPayments from './components/pages/checkout/payment';

import AcceptAlexaInvites from './components/pages/accept-alexa-invites';

import './App.css';
import './neighbors/layout/neighbors-interior-layout.less';
import NeighborsSidenav from './neighbors/components/NeighborsSidenav/NeighborsSidenav';
import { NeighborsNavProvider } from './neighbors/contexts/NeighborsNavContext';
import { isNeighborsAuthenticatedAppRoute } from './neighbors/layout/neighborsInteriorPaths';
import 'semantic-ui-less/semantic.less';
import 'react-toastify/dist/ReactToastify.css';
import './utils/Toast/toast.less';
import { Steps, Hints } from 'intro.js-react';
import 'intro.js/introjs.css';
import LinkResidentList from './components/pages/link-resident-list';
import InviteAlexaUser from './components/pages/invite-alexa-user';
import AlexaInviteSSORedirect from './components/pages/accept-alexa-invites/redirect';
import FNFInviteeSignup from './components/pages/fnfInviteeSignup';
import StaffEnteredDetail from './components/components/staffEnteredDetail';
import CustomSidebar from './components/components/CustomSidebar/CustomSidebar';
import './assets/fontawesome/css/fontawesome.css'; // parent css class required for child css to work
import './assets/fontawesome/css/light.css'; // import the relevant child css classes manually according to usage e.g fa-thin, fa-sharp etc.
import './assets/fontawesome/css/thin.css';
import './assets/fontawesome/css/sharp-light.css';
import './assets/fontawesome/css/solid.css';
import Documents from './neighbors/pages/documents';
import OrderStatus from './neighbors/pages/order-status';
import CreateOrder from './neighbors/pages/order-status/create';
import MyProfile from './neighbors/pages/my-profile';
import ResidentSocial from './neighbors/pages/resident-social';
import Survey from './neighbors/pages/survey';
import { neighborsLogoPath } from './utils/logoPaths';
import Announcements from './neighbors/pages/announcements';
import CommunityNews from './neighbors/pages/community-news';
import Calendar from './neighbors/pages/calendar';
import Dining from './neighbors/pages/dining';
import MenuPageForRestaurant from './neighbors/components/Dining/MenuPageForRestaurant';
import OrderBookingPage from './neighbors/components/Dining/OrderBookingPage';
import Menu from './neighbors/pages/menu';
import Links from './neighbors/pages/links';
import NeighborsGreetings from './neighbors/pages/neighbors-greetings';
import { CartProvider } from './neighbors/contexts/cart/Provider';
import { FacilityProvider } from './neighbors/contexts/facility/Provider';
import FacilityGate from './neighbors/components/FacilityGate';
import NeighborsHome from './neighbors/pages/neighbors-home';
import NeighborsPayments from './neighbors/pages/payments';
import { normalizeDefaultNeighborsLanding } from './types/userProfile';
import { AuthenticatedRoutesProps, NeighborsAppRouteProps } from './types/routes';
import GuestCompleteProfile from './neighbors/pages/guest-complete-profile';
import GuestPending from './neighbors/pages/guest-pending';
import Welcome from './neighbors/pages/welcome';
import { fetchNeighborsIdentity } from './neighbors/services/Identity';
import { resolveGuestLandingPath } from './neighbors/utils/guestAccessRouting';
import { getMembersOrigin, getNeighborsOrigin } from './utils/platformOrigins';
interface Props extends RouteComponentProps {}

interface userObjectInterface {
    cognitoUser: any;
    profile: any;
    registrants: any;
    username: any;
    authenticated: boolean;
    userType?: string;
    guestAccess?: any;
    guestIdentity?: any;
    needsOnboarding?: boolean;
}

const uninitiatedUserObject: userObjectInterface = {
    cognitoUser: {},
    profile: undefined,
    registrants: {
        list: [],
        activeRegistrantsIndexes: [],
    },
    username: undefined,
    authenticated: false,
    userType: '',
};

const steps = [
    {
        element: '.feed-parent',
        intro: 'Welcome to your Feed. View all messages, requests, and activity of your Alexa Users here. Filter to view indvidual features and even download reports. If you accept a request, it will be added to your “My Requests” row.',
        position: 'top',
    },
    {
        element: '.Feedbutton',
        intro: 'Your feed. View messages, activities, and requests here',
        tooltipClass: 'feed-button-offset',
    },
    {
        element: '.Messagesbutton',
        intro: 'Text-style view of messages. See entire history conversations. ',
    },
    {
        element: '.Remindersbutton',
        intro: 'Set and manage reminders for your Alexa Users.',
    },
    {
        element: '.Send.Messagebutton',
        intro: 'Send individual or group messages directly',
    },
    {
        element: '.Familybutton',
        intro: 'Create your Circle of Trust. Add friends, family, and more via text or email. ',
    },
    {
        element: '.Alexa.Usersbutton',
        intro: 'Connect or add Alexa user(s). Sign in with their Amazon account',
    },
    {
        element: '.Settingsbutton',
        intro: 'Adjust your app settings including Notifications and message archives.',
        tooltipClass: 'feed-button-offset',
    },
    {
        element: '.resident-links',
        intro: 'The top row are your Alexa users. Select one, mutiple, or all to interact with',
        tooltipClass: 'resident-links-tooltip',
    },
];

const NeighborsDefaultLandingRedirect = () => {
    const {
        userObject: { profile, userType },
    } = useContext(UserContext);

    if (userType === 'guest') {
        return <Redirect to={{ pathname: '/home' }} />;
    }

    const defaultNeighborsLanding = normalizeDefaultNeighborsLanding(profile?.defaultNeighborsLanding);
    const path = defaultNeighborsLanding === 'social' ? '/resident-social' : '/home';

    return <Redirect to={{ pathname: path }} />;
};

const getGuestPostLoginPath = (guestUserObject) =>
    resolveGuestLandingPath(guestUserObject.guestAccess, guestUserObject.needsOnboarding);

// Navigating cross-origin is a side effect and shouldn't run during render - do it in an
// effect instead, same as any other redirect-on-mount component.
const CrossOriginRedirect = ({ url }: { url: string }) => {
    useEffect(() => {
        window.location.href = url;
    }, [url]);
    return null;
};

const isManager = (userObject: userObjectInterface) => {
    if (userObject && userObject.registrants) {
        return userObject.registrants.list.find((registrant) => registrant.manager);
    }
};

const isCommunityManager = (userObject: userObjectInterface) => {
    if (userObject && userObject.registrants) {
        if (process.env.REACT_APP_PLATFORM === 'community') {
            const managedResident = userObject.registrants.list.find((registrant) => {
                return registrant.Facility && (registrant.manager || registrant.isCommunityManager)
            })
            if (managedResident) {
                return true
            }
            return false
        }
        return userObject.registrants.list.find((registrant) => registrant.isCommunityManager);
    }
};

const RoutesComponent = ({ isOnNeighborsAppRoute }: NeighborsAppRouteProps) => {
    const { userObject } = useContext(UserContext);
    const [credentials, setCredentials] = useState({});
    if (process.env.REACT_APP_PLATFORM === 'neighbors' || process.env.REACT_APP_PLATFORM === 'members') {
        /*
            Neighbor's/members' web app routes and components:
            Its different from community and speak2family web app.
            Shared verbatim between neighbors (residents/relationship managers) and members
            (guests) - isGuestUser/isRelationshipManager below is what actually decides which
            routes render, and the bootstrap effect already guarantees userObject's userType
            matches whichever platform is running before this component ever mounts.
        */
        /*
            If the user is Relationship manager to a resident, then show the neighbors interface.
                Else show a message stating they are not a relationship manager along with the invite screen and the
                My profile screen to allow log out
        */
      const isGuestUser = userObject.userType === 'guest';
      const isRelationshipManager = isManager(userObject)

      // Shared by both the relationship-manager/guest shell below and the plain-resident
      // fallback shell further down - an unauthenticated visit to either platform falls
      // through to whichever shell applies, so both /login routes must stay in sync.
      const renderLoginRoute = (routeProps: RouteComponentProps) => {
          if (userObject.authenticated) return <Redirect to={{ pathname: '/' }} />;
          const loginParams = queryString.parse(routeProps.location.search);
          if (process.env.REACT_APP_PLATFORM === 'neighbors' && loginParams.mode === 'guest') {
              // Guest sign-in/sign-up now lives exclusively on members.speak2family.com.
              // (hasGuestSocialLoginInProgress() is not checked here: that flag is written to
              // sessionStorage on members.speak2family.com before the Cognito redirect, and
              // sessionStorage doesn't cross origins, so it can never read true on neighbors.)
              return <CrossOriginRedirect url={`${getMembersOrigin()}/login${routeProps.location.search || ''}`} />;
          }
          if (process.env.REACT_APP_PLATFORM === 'members' && loginParams.mode === 'resident') {
              // Resident/staff sign-in stays exclusively on neighbors.speak2family.com.
              return <CrossOriginRedirect url={`${getNeighborsOrigin()}/login`} />;
          }
          if (process.env.REACT_APP_PLATFORM === 'neighbors') {
              // Neighbors keeps its original login flow as the default landing - never bounce to Welcome.
              return <LogIn {...routeProps} />;
          }
          const forceLogin = loginParams.mode === 'guest'
              || loginParams.mode === 'resident'
              || Authentication.hasGuestSocialLoginInProgress();
          return forceLogin
              ? <LogIn {...routeProps} />
              : <Redirect to={{ pathname: '/welcome', search: routeProps.location.search }} />;
      };

      if (isRelationshipManager || isGuestUser) {
        const guestNeedsOnboarding = Boolean(userObject.needsOnboarding);
        return (
            <div>
                <Sidebar.Pushable
                    style={{ marginTop: '0px' }}
                    as={Segment}
                >
                    <Grid padded={!isOnNeighborsAppRoute} style={{ justifyContent: 'center' }}>
                        <Grid.Row columns={userObject.authenticated && !isOnNeighborsAppRoute ? 2 : 1}>
                            {userObject.authenticated && !isOnNeighborsAppRoute && (
                                <Grid.Column width={2} style={{ padding: '0'}}>
                                    <CustomSidebar />
                                </Grid.Column>
                            )}
                            <Grid.Column
                                width={userObject.authenticated && !isOnNeighborsAppRoute ? 13 : 15}
                                style={isOnNeighborsAppRoute ? { padding: 0, marginLeft: 0 } : { padding: '20px 0 0 0', marginLeft: '15px' }}
                            >
                                <CartProvider>
                                    <Sidebar.Pusher>
                                        <Switch>
                                            {isGuestUser && <PrivateRoute path="/guest/complete-profile" component={GuestCompleteProfile} guestAllowed />}
                                            {isGuestUser && <PrivateRoute path="/guest/pending" component={GuestPending} guestAllowed />}
                                            {isGuestUser && guestNeedsOnboarding && <Redirect from="/*" to="/guest/complete-profile" />}
                                            {isGuestUser && <Redirect from="/guest/payments" to="/payments" />}
                                            {isGuestUser && <Redirect from="/guest/profile" to="/manage-profiles" />}
                                            {isGuestUser && <Redirect from="/guest/calendar" to="/calendar" />}
                                            {isGuestUser && <Redirect from="/guest/dining/order" to="/dining/order" />}
                                            {isGuestUser && (
                                                <Route
                                                    path="/guest/dining/:restaurantId/:menuId"
                                                    render={(routeProps: RouteComponentProps<{ restaurantId: string; menuId: string }>) => (
                                                        <Redirect to={`/dining/${routeProps.match.params.restaurantId}/${routeProps.match.params.menuId}`} />
                                                    )}
                                                />
                                            )}
                                            {isGuestUser && (
                                                <Route
                                                    path="/guest/dining/:restaurantId"
                                                    render={(routeProps: RouteComponentProps<{ restaurantId: string }>) => (
                                                        <Redirect to={`/dining/${routeProps.match.params.restaurantId}`} />
                                                    )}
                                                />
                                            )}
                                            {isGuestUser && <Redirect from="/guest/dining" to="/dining" />}
                                            {isGuestUser && <Redirect from="/guest/orders" to="/create-order" />}
                                            {isGuestUser && <Redirect from="/guest/order-status" to="/order-status" />}
                                            <PrivateRoute exact={true} path="/" component={NeighborsDefaultLandingRedirect} guestAllowed />
                                            <PrivateRoute path="/home" component={NeighborsHome} guestAllowed />
                                            <PrivateRoute path="/documents" component={Documents} />
                                            <PrivateRoute path="/order-status" component={OrderStatus} guestAllowed />
                                            <PrivateRoute path="/create-order" component={CreateOrder} guestAllowed />
                                            <PrivateRoute path="/dining/order" component={OrderBookingPage} guestAllowed />
                                            <PrivateRoute path="/dining/:restaurantId/:menuId"component={OrderBookingPage} guestAllowed />
                                            <PrivateRoute path="/dining/:restaurantId" component={MenuPageForRestaurant} guestAllowed />
                                            <PrivateRoute path="/dining" component={Dining} guestAllowed />
                                            <PrivateRoute path="/manage-profiles" component={MyProfile} guestAllowed />
                                            <PrivateRoute path="/announcements" component={Announcements} />
                                            <PrivateRoute path="/resident-social" component={ResidentSocial} />
                                            <PrivateRoute path="/calendar" component={Calendar} guestAllowed />
                                            <PrivateRoute path="/menu" component={Menu} guestAllowed />
                                            {/* Shared /payments route - runs for residents on neighbors and guests on members. */}
                                            <PrivateRoute path="/payments" component={NeighborsPayments} guestAllowed />
                                            <PrivateRoute path="/community-news" component={CommunityNews} guestAllowed />
                                            <PrivateRoute path="/survey" component={Survey} />
                                            <PrivateRoute path="/links" component={Links} />
                                            <PrivateRoute path="/greetings" component={NeighborsGreetings} />
                                            <PrivateRoute path="/invites" component={Invites} />
                                            <PrivateRoute path={"/addressbook/staff/:relativeid"} component={StaffEnteredDetail} />
                                            <PrivateRoute path="/addressbook/:relativeId" component={RelativeDetail} />
                                            <PrivateRoute path="/addressbook" component={FamilyAndFriends} />
                                            <Route
                                                exact
                                                path="/login"
                                                render={renderLoginRoute}
                                            />
                                            <Route
                                                path="/login/:token/:registrantId?/:receivedContact?"
                                                render={
                                                    userObject.authenticated
                                                        ? () => <Redirect to={{ pathname: '/' }} />
                                                        : (props) => <LogIn {...props} />
                                                }
                                            />
                                            <Route
                                                path="/forgot-password"
                                                render={
                                                    userObject.authenticated
                                                        ? () => <Redirect to={{ pathname: '/' }} />
                                                        : (props) => <ForgotPasswordPage {...props} />
                                                }
                                            />
                                            <Route
                                                path="/change-forgotten-password"
                                                render={
                                                    userObject.authenticated
                                                        ? () => <Redirect to={{ pathname: '/' }} />
                                                        : (props) => <ChangeForgotPasswordPage {...props} />
                                                }
                                            />
                                            <Redirect from="/*" to="/" />
                                            <Route render={() => <h1>404 Not Found</h1>} />
                                        </Switch>
                                    </Sidebar.Pusher>
                                </CartProvider>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Sidebar.Pushable>
            </div>
        );
      } else { // Below is the list of screens accessible to a person who is not a relationship manager. They should not access Neighbors web features and are only allowed to view the specific pages listed below.
        return (
            <Switch>
                <Route
                    exact
                    path="/login"
                    render={renderLoginRoute}
                />
                <Route
                    path="/login/:token/:registrantId?/:receivedContact?"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/' }} />
                            : (props) => <LogIn {...props} />
                    }
                />
                {process.env.REACT_APP_PLATFORM === 'members' && (
                    <Route
                        path="/welcome"
                        render={
                            userObject.authenticated
                                ? () => <Redirect to={{ pathname: '/' }} />
                                : (props) => <Welcome {...props} />
                        }
                    />
                )}
                <PrivateRoute
                    path="/invites"
                    component={(props) => (
                        <Segment
                            style={{
                                margin: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '80%',
                            }}
                        >
                            <Invites {...props} />
                            <Message info style={{width: '100%', textAlign: 'center'}}>You are not a relationship manager to anyone, please contact support to be invited as a relationship manager</Message>
                        </Segment>
                    )}
                />
                <Route
                    path="/forgot-password"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/invites' }} />
                            : (props) => <ForgotPasswordPage {...props} />
                    }
                />
                <Route
                    path="/change-forgotten-password"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/invites' }} />
                            : (props) => <ChangeForgotPasswordPage {...props} />
                    }
                />
                <Redirect from="/" to="/invites" />
                <Redirect from="/*" to="/invites" />
                <Route render={() => <h1>404 Not Found</h1>} />
            </Switch>
        );
      }      
    }

    if (process.env.REACT_APP_PLATFORM === 'community') {
        /*
        Skipping the following routes and components for Speak2 Community Website (as opposed to S2F website):
        Route, Component
        - /settings, Settings
        - /activities/:activityId, ActivityDetail
        - /archives, ArchivedActivities
        - /activities, Activities
        - /reminders, Reminders
        - /alexa-linking, AlexaLinkingLogin
        - /link-resident-list, LinkResidentList
        - /downloads, Downloads
        - /sso, SSOAlexaUser
        - /sso-redirect, SSORedirect
        - /checkout, Checkout
        - /public/checkout/:paymentManagerId, PublicCheckout
        - /payments, Payments
        - /public/checkout-success, CheckoutSuccess
        - /invite-alexa-user, InviteAlexaUser
        - /alexa-invite-sso-redirect, AlexaInviteSSORedirect


        Renaming the following routes for Speak2 Community Website (as opposed to S2F website):
        Old route, New route
        - /news, /send-message
        - /family_friends, /addressbook
        - /community-activities, /activities
        - /community-menus, /menu

        Changes in redirects:
        Route - Old redirect route, Old component - New redirect route, New Component
        - / - /activities, Activities - /send-message, News
        - /* - /activities, Activities - /send-message, News



        */
        
        return (
            <div style={{ maxWidth: "900px", margin: "auto" }}>
                <Switch>
                    <PrivateRoute path="/send-message" component={News} />
                    <PrivateRoute path={"/addressbook/staff/:relativeid"} component={StaffEnteredDetail} />
                    <PrivateRoute path="/addressbook/:relativeId" component={RelativeDetail} />
                    <PrivateRoute path="/addressbook" component={FamilyAndFriends} />
                    <PrivateRoute path="/profile-settings" component={ProfileSettings} />
                    <PrivateRoute path="/message-recipients/:messageRecipientId/address-book/contacts/:contactId" component={AddressBookContactDetails} />
                    <PrivateRoute path="/message-recipients/:messageRecipientId/address-book" component={AddressBookDetails} />
                    <PrivateRoute path="/message-recipients/create" component={CreateMessageRecipient} />
                    <PrivateRoute
                        path="/message-recipients/:messageRecipientId"
                        component={MessageRecipientDetails}
                    />
                    <PrivateRoute path="/message-recipients" component={MessageRecipients} />
                    <PrivateRoute path="/messages/:messageRecipientId" component={MessageReplyDetails} />
                    <PrivateRoute path="/messages" component={Messages} />
                    <PrivateRoute path="/requests/:requestId" component={RequestDetail} />

                    <PrivateRoute
                        path="/activities"
                        component={
                            isCommunityManager(userObject)
                                ? CommunityActivities
                                : () => <Redirect to={{ pathname: '/send-message' }} />
                        }
                    />
                    <PrivateRoute
                        path="/menu"
                        component={
                            isCommunityManager(userObject)
                                ? CommunityMenus
                                : () => <Redirect to={{ pathname: '/send-message' }} />
                        }
                    />
                    <PrivateRoute
                        path="/notifications"
                        component={
                            isCommunityManager(userObject)
                                ? CommunityNotifications
                                : () => <Redirect to={{ pathname: '/send-message' }} />
                        }
                    />

                    <PrivateRoute
                        path="/support"
                        component={Support}
                    />
                    <Route
                        path="/login/:token?/:registrantId?/:receivedContact?"
                        render={
                            userObject.authenticated
                                ? () => <Redirect to={{ pathname: '/send-message' }} />
                                : (props) => <LogIn {...props} />
                        }
                    />
                    {/* <Route
                    path="/signup"
                    component={(props) => (
                        <SignUpComponent {...props} credentials={credentials} setCredentials={setCredentials} />
                    )}
                /> */}
                    {/* <Route
                    path="/signup"
                    render={(props) => (
                        <Signup {...props} credentials={credentials} setCredentials={setCredentials} />
                    )}
                /> */}
                    <Route
                        path="/forgot-password"
                        render={
                            userObject.authenticated
                                ? () => <Redirect to={{ pathname: '/send-message' }} />
                                : (props) => <ForgotPasswordPage {...props} />
                        }
                    />
                    <Route
                        path="/change-forgotten-password"
                        render={
                            userObject.authenticated
                                ? () => <Redirect to={{ pathname: '/send-message' }} />
                                : (props) => <ChangeForgotPasswordPage {...props} />
                        }
                    />
                    <Route path="/downloads/community" component={CommunityDownloads} />
                    <Route path="/auto-login" component={AutoLogin} />

                    <PrivateRoute path="/invites" component={Invites} />
                    <Route path="/accept-alexa-invites/:token" component={AcceptAlexaInvites} />
                    <Route path="/signup-fnf-invite" component={FNFInviteeSignup} />

                    <Redirect from="/" to="/send-message" />
                    <Redirect from="/*" to="/send-message" />
                    <Route render={() => <h1>404 Not Found</h1>} />
                </Switch>
            </div>
        );

    }

    return (
        <div style={{ maxWidth: "900px", margin: "auto" }}>
            <Switch>
                <PrivateRoute path="/news" component={News} />
                <PrivateRoute
                    path="/settings"
                    component={isManager(userObject) ? Settings : () => <Redirect to={{ pathname: '/activities' }} />}
                />
                <PrivateRoute path="/family_friends/:relativeId" component={RelativeDetail} />
                <PrivateRoute path="/family_friends" component={FamilyAndFriends} />
                <PrivateRoute path="/profile-settings" component={ProfileSettings} />
                <PrivateRoute path="/message-recipients/:messageRecipientId/address-book/contacts/:contactId" component={AddressBookContactDetails} />

                <PrivateRoute path="/message-recipients/:messageRecipientId/address-book" component={AddressBookDetails} />
                <PrivateRoute path="/message-recipients/create" component={CreateMessageRecipient} />
                <PrivateRoute
                    path="/message-recipients/:messageRecipientId"
                    component={MessageRecipientDetails}
                />
                <PrivateRoute path="/message-recipients" component={MessageRecipients} />
                <PrivateRoute path="/messages/:messageRecipientId" component={MessageReplyDetails} />
                <PrivateRoute path="/messages" component={Messages} />
                <PrivateRoute path="/requests/:requestId" component={RequestDetail} />
                <PrivateRoute path="/activities/:activityId" component={ActivityDetail} />
                <PrivateRoute path="/archives" component={ArchivedActivities} />
                <PrivateRoute path="/activities" component={Activities} />
                <PrivateRoute
                    path="/community-activities"
                    component={
                        isCommunityManager(userObject)
                            ? CommunityActivities
                            : () => <Redirect to={{ pathname: '/activities' }} />
                    }
                />
                <PrivateRoute
                    path="/community-menus"
                    component={
                        isCommunityManager(userObject)
                            ? CommunityMenus
                            : () => <Redirect to={{ pathname: '/activities' }} />
                    }
                />
                <PrivateRoute path="/reminders" component={Reminders} />

                <Route path="/alexa-linking" component={AlexaLinkingLogin} />
                <PrivateRoute path="/link-resident-list" component={LinkResidentList} />

                <Route
                    path="/login/:token?/:registrantId?/:receivedContact?"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/activities' }} />
                            : (props) => <LogIn {...props} />
                    }
                />
                <Route
                    path="/signup"
                    render={(props) => (
                        <Signup {...props} credentials={credentials} setCredentials={setCredentials} />
                    )}
                />
                <Route
                    path="/forgot-password"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/activities' }} />
                            : (props) => <ForgotPasswordPage {...props} />
                    }
                />
                <Route
                    path="/change-forgotten-password"
                    render={
                        userObject.authenticated
                            ? () => <Redirect to={{ pathname: '/activities' }} />
                            : (props) => <ChangeForgotPasswordPage {...props} />
                    }
                />
                <Route path="/downloads/community" component={CommunityDownloads} />
                <Route path="/downloads" component={Downloads} />
                <Route path="/auto-login" component={AutoLogin} />
                <PrivateRoute path="/sso" component={SSOAlexaUser} />

                <Route path="/sso-redirect" component={SSORedirect} />
                <PrivateRoute path="/invites" component={Invites} />
                <PrivateRoute path="/checkout" component={Checkout} />
                <Route path="/public/checkout/:paymentManagerId" component={PublicCheckout}></Route>
                {/* Legacy /payments route for non-neighbors platforms. Neighbors/members use their own gated route above. */}
                <PrivateRoute path="/payments" component={LegacyPayments} />
                <Route path="/public/checkout-success" component={CheckoutSuccess}></Route>
                <PrivateRoute path="/invite-alexa-user" component={InviteAlexaUser} />
                <Route path="/accept-alexa-invites/:token" component={AcceptAlexaInvites} />
                {/* <Route path="/alexa-invite-accept-page" component={AlexaUserInvitationAcceptPage} /> */}
                <Route path="/signup-fnf-invite" component={FNFInviteeSignup} />
                <Route path="/alexa-invite-sso-redirect" component={AlexaInviteSSORedirect} />
                <Redirect from="/" to="/activities" />
                <Redirect from="/*" to="/activities" />
                <Route render={() => <h1>404 Not Found</h1>} />
            </Switch>
        </div>
    );
}

const AuthenticatedRoutes = ({
    isOnNeighborsAppRoute,
    showSidebarProp,
    ...routeProps
}: AuthenticatedRoutesProps) => {
    const { userObject } = useContext(UserContext);
    const showNeighborsAppShell = isOnNeighborsAppRoute && (isManager(userObject) || userObject.userType === 'guest');
    const routes = <RoutesComponent isOnNeighborsAppRoute={isOnNeighborsAppRoute} />;
    // Facility details are only fetched on neighbors. Guests on members still use this shell,
    // but gating them would block payments and the rest of the members app on an empty facility.
    const gateFacility = showNeighborsAppShell && userObject.userType !== 'guest';

    return (
        <div className={showNeighborsAppShell ? 'neighbors-interior-layout' : undefined}>
            {showNeighborsAppShell && <NeighborsSidenav />}
            <div className={showNeighborsAppShell ? 'neighbors-interior-shell' : undefined}>
                <Header {...routeProps} showSidebarProp={showSidebarProp} isOnNeighborsAppRoute={isOnNeighborsAppRoute} />
                {gateFacility ? <FacilityGate>{routes}</FacilityGate> : routes}
            </div>
        </div>
    );
};

const App: FC<Props> = (props) => {
    const [userObject, setUserObject] = useState(uninitiatedUserObject);
    const [loading, setLoading] = useState<boolean>(true);
    const [stepsEnabled, setStepsEnabled] = useState(false);
    const [showSidebarProp, setShowSidebarProp] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const isOnNeighborsAppRoute = isNeighborsAuthenticatedAppRoute(props.location.pathname);
    const activeIndex = userObject.registrants.activeRegistrantsIndexes?.[0];
    const activeResidentId =
        typeof activeIndex === 'number'
            ? userObject.registrants.list?.[activeIndex]?._id
            : activeIndex;

    const setUser = (userObject) => {
        setUserObject(userObject);
    };

    const toggleRedirect = (redirect) => {
        props.history.push(redirect);
    };

    const buildGuestUserObject = (cognitoUser, identity) => {
        const profile = identity && identity.profile ? identity.profile : null;
        return {
            cognitoUser,
            profile,
            username: profile?.Username || profile?.Email || cognitoUser?.username,
            registrants: {
                list: profile ? [profile] : [],
                activeRegistrantsIndexes: profile ? [0] : [],
            },
            authenticated: true,
            userType: 'guest',
            guestAccess: identity?.access,
            guestIdentity: identity,
            needsOnboarding: Boolean(identity?.needsOnboarding),
        };
    };

    const reloadRegistrantLists = async () => {
        const connectedRegistrants = await Database.getRelatedRegistrants();

        let activeRegistrantsIndexes =
            (userObject.registrants &&
                userObject.registrants.activeRegistrantsIndexes &&
                userObject.registrants.activeRegistrantsIndexes[0]) ||
            0;
        if (process.env.REACT_APP_PLATFORM === 'neighbors') { // If the env is neighbors, then set active registrant to the managed resident
            const managedResident = userObject.registrants ? userObject.registrants.list.find((registrant) => registrant.manager) : null;
            const indexOfManagedResident = managedResident ? userObject.registrants.list.indexOf(managedResident) : 0;
            activeRegistrantsIndexes = indexOfManagedResident;
        }

        const registrants = {
            list: connectedRegistrants.residents,
            activeRegistrantsIndexes:
                connectedRegistrants.residents && connectedRegistrants.residents.length
                    ? [activeRegistrantsIndexes]
                    : [],
        };
        setUserObject({
            ...userObject,
            registrants,
        });
    };

    useEffect(() => { // Conditionally render browser's title and icon based on the community and neighbors web
        const isNeighborsOrMembers = process.env.REACT_APP_PLATFORM === 'neighbors' || process.env.REACT_APP_PLATFORM === 'members';
        const title = document.getElementById('pageTitle');
        if (title && isNeighborsOrMembers) {
            title.innerText = process.env.REACT_APP_PLATFORM === 'members' ? 'Speak2 Members' : 'Speak2 Neighbors';
        }
        const largeIcon = document.getElementById('largeRelIcon') as HTMLLinkElement | null;
        const smallIcon = document.getElementById('smallRelIcon') as HTMLLinkElement | null;
        if (largeIcon && isNeighborsOrMembers) {
            largeIcon.href = neighborsLogoPath;
        }
        if (smallIcon && isNeighborsOrMembers) {
            smallIcon.href = neighborsLogoPath;
        }
        // DM Sans/Lora are only used by the guest-design-tokens styles (login, guest onboarding,
        // payments, welcome) - load them here instead of unconditionally in index.html so
        // community/speak2family builds don't pay for a font they never reference.
        if (isNeighborsOrMembers && !document.getElementById('guestFontStylesheet')) {
            const preconnectGoogleapis = document.createElement('link');
            preconnectGoogleapis.rel = 'preconnect';
            preconnectGoogleapis.href = 'https://fonts.googleapis.com';
            document.head.appendChild(preconnectGoogleapis);

            const preconnectGstatic = document.createElement('link');
            preconnectGstatic.rel = 'preconnect';
            preconnectGstatic.href = 'https://fonts.gstatic.com';
            preconnectGstatic.crossOrigin = 'anonymous';
            document.head.appendChild(preconnectGstatic);

            const guestFontStylesheet = document.createElement('link');
            guestFontStylesheet.id = 'guestFontStylesheet';
            guestFontStylesheet.rel = 'stylesheet';
            guestFontStylesheet.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Lora:wght@500;600;700&display=swap';
            document.head.appendChild(guestFontStylesheet);
        }
    }, [])

    useEffect(() => {
        let mounted = true;
        const resolveAuthenticatedUser = async () => {
            // When returning from a social-login OAuth redirect, Amplify may still be
            // exchanging the ?code for tokens. Retry a few times so currentAuthenticatedUser
            // resolves before we fall through to the unauthenticated path (which causes the
            // white screen / redirect bounce on first login).
            const socialLoginInProgress = Authentication.hasGuestSocialLoginInProgress();
            const maxAttempts = socialLoginInProgress ? 6 : 1;
            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                try {
                    return await Auth.currentAuthenticatedUser({ bypassCache: true });
                } catch (error) {
                    if (attempt === maxAttempts - 1) throw error;
                    await new Promise((resolve) => setTimeout(resolve, 500));
                }
            }
            return null;
        };
        const bootstrapUser = async () => {
            try {
                const response = await resolveAuthenticatedUser();

                if (!response || response === 'not authenticated') {
                    setLoading(false);
                    if (props.history.location.pathname !== '/login') {
                        toggleRedirect(props.history.location.pathname + props.history.location.search);
                    }
                    return;
                }

                const accountType = response.attributes && response.attributes['custom:accountType'];

                if (process.env.REACT_APP_PLATFORM === 'members' && accountType !== 'relative') {
                    const identity = await fetchNeighborsIdentity();
                    if (!mounted) return;
                    const guestUserObject = buildGuestUserObject(response, identity);
                    setUserObject(guestUserObject);
                    Authentication.clearGuestSocialLogin();
                    if (props.history.location.pathname === '/' || props.history.location.pathname === '/login') {
                        props.history.replace(getGuestPostLoginPath(guestUserObject));
                    }
                    setLoading(false);
                    setDataLoaded(true);
                    return;
                }

                if (process.env.REACT_APP_PLATFORM === 'neighbors' && accountType !== 'relative') {
                    // Guests share this Cognito pool but belong on members.speak2family.com now.
                    // Amplify's session lives in this origin's localStorage, so sign out here first -
                    // otherwise the guest lands on members unauthenticated while neighbors still
                    // thinks they're signed in, and every future visit just bounces them again.
                    clearAllGreetingsSummaryCache();
                    await Auth.signOut().catch(() => {});
                    window.location.href = getMembersOrigin();
                    return;
                }

                if (process.env.REACT_APP_PLATFORM === 'members' && accountType === 'relative') {
                    // Residents/staff belong on neighbors.speak2family.com. Same reasoning as above.
                    clearAllGreetingsSummaryCache();
                    await Auth.signOut().catch(() => {});
                    window.location.href = getNeighborsOrigin();
                    return;
                }

                if (accountType !== 'relative') {
                    clearAllGreetingsSummaryCache();
                    await Auth.signOut();
                    setLoading(false);
                    return;
                }

                const cognitoUser = response;
                const profileResponse = await Database.getUserDbProfile();
                const profile = profileResponse.userProfile;
                const connectedRegistrants = await Database.getRelatedRegistrants();
                const userObject = {
                    cognitoUser,
                    profile,
                    username: profileResponse.Username,
                    registrants: {
                        list: connectedRegistrants.residents,
                        activeRegistrantsIndexes:
                            connectedRegistrants.residents && connectedRegistrants.residents.length ? [0] : [],
                    },
                    authenticated: true,
                    userType: 'resident',
                };
                if (process.env.REACT_APP_PLATFORM === 'neighbors') {
                    const managedResident = userObject && userObject.registrants && userObject.registrants.list && userObject.registrants.list.find((registrant) => {
                        return registrant.Facility && (registrant.manager || registrant.isCommunityManager)
                    })
                    if (managedResident) {  // If the env is neighbors, then set active registrant to the managed resident
                        const indexOfManagedResident = userObject.registrants.list.indexOf(managedResident);
                        userObject.registrants.activeRegistrantsIndexes = [indexOfManagedResident];
                    }
                }
                if (!mounted) return;
                setUserObject(userObject);
                setLoading(false);
                // if (props.history.location.pathname !== '/login') {
                //   	toggleRedirect(props.history.location.pathname + props.history.location.search);
                // }
                setDataLoaded(true);
            } catch (error) {
                setLoading(false);
                if (props.history.location.pathname !== '/login') {
                    toggleRedirect(props.history.location.pathname + props.history.location.search);
                }
            }
        };

        bootstrapUser();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        //using data loaded is a precautionary step which might not be necessarily be needed.
        dataLoaded &&
            !localStorage.getItem('feedIntroShown') &&
            props.history.location.pathname === '/activities' &&
            setTimeout(() => {
                window.innerWidth > 600 && setShowSidebarProp(true);
                setStepsEnabled(true);
            }, 200);
    }, [props.history.location.pathname, dataLoaded]);

    const isPublicRoute = (props: RouteComponentProps) => {
        const pathname = props.location.pathname;
        return ['/signup', '/checkout', '/auto-login', '/public/checkout-success'].includes(pathname) ||
            pathname.includes('/public/checkout') ||
            pathname.includes('/accept-alexa-invites/') ||
            pathname === '/guest/complete-profile';
    }

    return (
        <UserContext.Provider value={{ userObject, setUser, reloadRegistrantLists }}>
            {process.env.REACT_APP_PLATFORM === 'community'
                ?   <Helmet>
                        <title>Speak2 Community</title>
                    </Helmet>
                :   ''
            }
            <RedirectContext.Provider value={toggleRedirect}>
                <NeighborsNavProvider>
                <FacilityProvider>
                    {loading ? (
                        <Dimmer active={loading} inverted>
                            <Loader active={loading} />
                        </Dimmer>
                    ) : (
                        <>
                            {userObject.authenticated && !isPublicRoute(props)
                            ? <AuthenticatedRoutes key={String(activeResidentId || '')} {...props} isOnNeighborsAppRoute={isOnNeighborsAppRoute} showSidebarProp={showSidebarProp} />
                            : <RoutesComponent isOnNeighborsAppRoute={isOnNeighborsAppRoute} />}
                            {(process.env.REACT_APP_PLATFORM !== "neighbors" && process.env.REACT_APP_PLATFORM !== "members") ? <Steps
                                enabled={stepsEnabled}
                                steps={steps}
                                initialStep={0}
                                onExit={() => {
                                    setShowSidebarProp(false);
                                    localStorage.setItem('feedIntroShown', 'yes');
                                }}
                                onBeforeChange={(nextStepIndex) => {
                                    if (nextStepIndex == 1) {
                                        setShowSidebarProp(true);
                                    }
                                    if (window.innerWidth <= 600) {
                                        if (nextStepIndex == 8) {
                                            setShowSidebarProp(false);
                                        } 
                                        if (nextStepIndex == 7) {
                                            setShowSidebarProp(true);
                                        }
                                    }
                                }}
                            /> : <></>}
                        </>
                    )}

                    <ToastContainer
                        position="bottom-center"
                        closeButton={<ToastCloseButton />}
                        hideProgressBar
                        pauseOnHover={false}
                        closeOnClick={false}
                        draggable
                    />
                    <ToastBehavior />
                </FacilityProvider>
                </NeighborsNavProvider>
            </RedirectContext.Provider>
        </UserContext.Provider>
    );
};

export default withRouter(App);
